<template>
    <section class="card-list-template" :style="getStyle" v-if="load">
        <CommonTitleTemplate
            :title="title"
            :tag-list="tagList"
            :defaultTag="defaultTag"
            :tag-config="tagConfig"
            @goMore="moreHandle"
            @changeTag="changeTag"
        >
        </CommonTitleTemplate>
        <div class="card-list-wrap" v-if="showList?.length">
            <div
                v-for="(item, index) of showList"
                :key="index"
                :style="itemStyle"
                :class="[
                    'card-item',
                    {
                        'card-item-img-top': imgPos === 'top',
                        'card-item-img-left': imgPos === 'left',
                        'card-item-img-right': imgPos === 'right',
                        'card-item-img-bottom': imgPos === 'bottom',
                        'card-item-img-full': imgPos === 'full',
                    },
                ]"
                @click="goDetail(item)"
            >
                <div class="card-item-img">
                    <LazyImage :src="item[coverKey]" className="img" />
                </div>
                <div class="card-item-content">
                    <div class="title">
                        <Ellipsis :rows="2" :content="item[contentTitleKey] ?? '-'" />
                    </div>
                    <div v-for="(info, idx) of extensions" :key="idx + info.key[0]">
                        <div v-if="info.type === 'dateRange'" class="extension-info">
                            {{ info.label }}：{{
                                item[info.key[0]]
                                    ? dayjs(item[info.key[0]]).format(info.format ?? 'YYYY.MM.DD')
                                    : ''
                            }}-{{
                                item[info.key[1]]
                                    ? dayjs(item[info.key[1]]).format(info.format ?? 'YYYY.MM.DD')
                                    : ''
                            }}
                        </div>
                        <TimeShowTemplate
                            v-else-if="info.type === 'time'"
                            :label="info.label"
                            :time="
                                item[info.key as string]
                                    ? dayjs(item[info.key as string]).format(
                                          info.format ?? 'YYYY.MM.DD',
                                      )
                                    : '-'
                            "
                        >
                        </TimeShowTemplate>
                        <div v-else class="extension-info">
                            {{ info.label }}：{{ getValueByPath(item, info.key as string) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <BigNoData v-else class="no-data" url="no-lesson.png" :info="`暂无数据`" />
    </section>
</template>

<script setup lang="ts">
import CommonTitleTemplate from '../common-title-template.vue';
import Ellipsis from '@/components/ellipsis.vue';
import BigNoData from '@/components/no-data.vue';
import LazyImage from '@/components/lazy-image/lazy-image.vue';
import TimeShowTemplate from '../time-show-template.vue';
import { computed, onMounted, PropType, reactive, ref } from 'vue';
import { isObject, isString } from 'lodash';
import dayjs from 'dayjs';
import { pageJump } from '@/global/location-transfer';
import { jumpHref } from '@/router/jumpHref';

type Extension = {
    label: string;
    key: string | string[];
    type?: 'dateRange' | 'text' | 'time';
    format?: string;
};

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    imgPos: {
        type: String as PropType<'top' | 'left' | 'right' | 'bottom' | 'full'>,
        default: 'top',
    },
    imgHeight: {
        type: String,
        default: '152px',
    },
    imgWidth: {
        type: String,
        default: '285px',
    },
    coverKey: {
        type: String,
        default: 'cover',
    },
    contentTitleKey: {
        type: String,
        default: 'title',
    },
    extensions: {
        type: Array as PropType<Array<Extension>>,
        default: () => [],
    },
    jumpType: {
        type: String as PropType<'activity' | 'mooc'>,
    },
    request: {
        type: [String, Object] as PropType<string | { name: string; resultKey: string }>,
        default: '',
    },
    requestParams: {
        type: Object as PropType<Record<string, any>>,
        default: () => ({}),
    },
    tagRequest: {
        type: [String, Object] as PropType<string | { name: string; resultKey: string }>,
        default: '',
    },
    tagRequestParams: {
        type: Object as PropType<Record<string, any>>,
        default: () => ({}),
    },
    jumpUrl: {
        type: Object as PropType<{
            path: string | Record<string, any>;
            params?: Array<string>;
        }>,
        default: () => ({}),
    },
    moreUrl: {
        type: Object as PropType<Record<string, any>>,
        default: () => ({}),
    },
    list: {
        type: Array as PropType<any[]>,
        default: () => [],
    },
    itemStyle: {
        type: String,
        default: '',
    },
    columnsNum: {
        type: Number,
        default: 4,
    },
    border: {
        type: Boolean,
        default: true,
    },
    tag: {
        type: Array as PropType<any[]>,
        default: () => [],
    },
    tagConfig: {
        type: Object as PropType<Record<string, any>>,
        default: () => ({}),
    },
});

const showList = ref<any[]>([]);

const filterData = reactive({
    pageIndex: 1,
    pageSize: 4,
});

const tagList = ref<any>([]);
const defaultTag = ref();

async function getTagList() {
    const { tagRequestParams = {}, tagRequest } = props;
    let list: any = [];
    if (!tagRequest) {
        list = props.tag || [];
        console.error('tagRequest is required');
    } else {
        const requestName = isString(tagRequest) ? tagRequest : tagRequest.name;
        await G_transmit(requestName, {
            params: {
                ...(tagRequestParams.filter?.type === 'query' ? filterData : {}),
                ...(tagRequestParams.params ?? {}),
            },
            data: {
                ...(tagRequestParams.filter?.type === 'body' ? filterData : {}),
                ...(tagRequestParams.data ?? {}),
            },
            message: false,
        }).then(res => {
            if (isObject(tagRequest)) {
                list = getValueByPath(res, tagRequest.resultKey) ?? [];
            } else if (res.data && res.data.rows) {
                list = res.data.rows || [];
            }
            // 配置键值名
            const key = props.tagConfig.key;
            const labelKey = props.tagConfig.label;
            // 过滤配置
            const includes = props.tagConfig?.filter?.includes || [];
            const excludes = props.tagConfig?.filter?.excludes || [];
            // 重组数组
            list = list.map(item => {
                if (key) {
                    item.value = item?.[key];
                    item.tagKey = item?.[key];
                }
                if (labelKey) {
                    item.tagName = item?.[labelKey];
                }
                if (excludes?.length)
                    item.checked = !excludes.some(({ key, value }) => item[key] === value);
                if (includes?.length)
                    item.checked = includes.some(({ key, value }) => item[key] === value);
                return item;
            });
        });
    }
    const defaultValue = list.find?.(item => item?.checked)?.value;
    defaultTag.value = defaultValue;
    tagList.value = list;
    await changeTag(defaultValue);
}

async function changeTag(value) {
    if (props.tagConfig?.valueKey) filterData[props.tagConfig?.valueKey] = value;
    await getList();
}

const getStyle = computed(() => {
    return `
        --img-height: ${props.imgHeight};
        --img-width: ${props.imgWidth};
        --content-width: ${['top', 'bottom'].includes(props.imgPos) ? props.imgWidth : 'auto'};
        --columns-num: ${props.columnsNum};
        --item-border: ${props.border ? '1px solid #eaeaea' : 'none'};
        `;
});

function getValueByPath(obj: any, path: string) {
    const keys = path.split('.');
    if (!keys.length) {
        return;
    }
    if (keys.length === 1) {
        return obj[path];
    }
    const value = keys.reduce((prev, current) => {
        return prev?.[current];
    }, obj);

    return value;
}

async function getList() {
    const { requestParams = {}, request } = props;
    if (!request) {
        showList.value = props.list;
        console.error('request is required');
    } else {
        const requestName = isString(request) ? request : request.name;
        await G_transmit(requestName, {
            params: {
                ...(requestParams.filter?.type === 'query' ? filterData : {}),
                ...(requestParams.params ?? {}),
            },
            data: {
                ...(requestParams.filter?.type === 'body' ? filterData : {}),
                ...(requestParams.data ?? {}),
            },
            message: false,
        }).then(res => {
            if (isObject(request)) {
                showList.value = getValueByPath(res, request.resultKey) ?? [];
            } else if (res.data && res.data.rows) {
                showList.value = res.data.rows || [];
            }
        });
    }
}

function goDetail(item) {
    const { jumpUrl } = props;
    if (!jumpUrl?.path) {
        console.error('jumpUrl is required');
        return;
    }

    if (isString(jumpUrl.path)) {
        const method = getValueByPath(jumpHref, jumpUrl.path);

        if (!method) {
            console.error('method is not exist');
            return;
        }

        pageJump(method(...(jumpUrl.params?.map(key => item[key]) ?? [])), true);

        return;
    }

    if (isObject(jumpUrl.path)) {
        pageJump(jumpUrl.path, true);
    }
}

function moreHandle() {
    const { moreUrl } = props;
    if (!moreUrl?.path) return;
    if (moreUrl?.searchQuerykeys?.length)
        moreUrl.path.query = Object.fromEntries(
            moreUrl.searchQuerykeys.map(key => [key, filterData[key]]),
        );
    pageJump(moreUrl.path, true);
}

const load = ref(false);

onMounted(async () => {
    await getTagList();
    // await getList();
    load.value = true;
});
</script>

<style lang="scss" scoped>
.card-list-template {
    .card-list-wrap {
        display: grid;
        grid-template-columns: repeat(var(--columns-num), 1fr);
        gap: 20px;
        margin-top: 26px;
    }

    .card-item {
        width: fit-content;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        flex-direction: column;

        &-img-left {
            flex-direction: row;
        }

        &-img-right {
            flex-direction: row-reverse;
        }

        &-img-bottom {
            flex-direction: column-reverse;
        }

        &-img-full {
            position: relative;
            min-height: var(--img-height);

            .card-item-img {
                position: absolute;
            }

            .card-item-content {
                position: relative;
                z-index: 1;
                color: #fff;
            }
        }

        &-img-left,
        &-img-right {
            .card-item-content {
                height: 100%;
            }

            .card-item-img {
                img {
                    height: 100%;
                }
            }
        }

        &:hover {
            box-shadow: 0px 1px 12px 0px rgba(0, 0, 0, 0.15);

            .title {
                color: #2164c9;
            }
        }
    }

    .card-item-img {
        flex-shrink: 0;
        width: var(--img-width);
        height: var(--img-height);
        overflow: hidden;
    }

    .card-item-content {
        width: var(--content-width);
        height: 132px;
        padding: 12px 20px 20px;
        border: var(--item-border);
    }

    .title {
        height: 48px;
        font-weight: bold;
        font-size: 16px;
        color: #000000;
    }

    .extension-info {
        margin-top: 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #666666;
        font-size: 14px;
        line-height: 14px;
    }
}
</style>
